import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AtlasApiService } from '@secca/core/services/atlas/atlas-api.service';
import { DocumentService } from '@secca/core/services/document.service';
import {
  DocumentEdit,
  DocumentEditAndReadDocument,
  DocumentEditJob,
  DocumentPIIEntity,
  DocumentPIIEntityInput,
  ReplaceDocumentPIIEntitiesRequest,
  SubmitDocumentTranslationRequest,
  UploadedDocumentResponse,
  UploadDocumentRequest
} from '@secca/shared/models/atlas/atlas-document-edit-and-read.model';
import { Observable, from } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class DocumentEditAndReadService {
  private readonly baseUrl = 'http://localhost:8081';
  private readonly documentType = 'MEDICAL_JOURNAL' as const;

  constructor(
    private http: HttpClient,
    private documentService: DocumentService,
    private atlasApiService: AtlasApiService
  ) {}

  prepareDocumentForTranslation(documentId: string, documentName: string, caseNumber: string): Observable<UploadedDocumentResponse> {
    return this.createUploadRequest(documentId, documentName, caseNumber).pipe(
      switchMap(request =>
        this.atlasApiService.withAtlasAuth(headers =>
          this.http.post<UploadedDocumentResponse>(this.atlasApiService.buildUrl('/api/v2/documents', this.baseUrl), request, { headers })
        )
      )
    );
  }

  getDocumentsForCase(caseNumber: string): Observable<DocumentEditAndReadDocument[]> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get<DocumentEditAndReadDocument[]>(this.atlasApiService.buildUrl('/api/v2/documents', this.baseUrl), {
        headers,
        params: new HttpParams().set('case_number', caseNumber)
      })
    );
  }

  getDocument(documentId: number, includeTextProperties = false): Observable<DocumentEditAndReadDocument> {
    let params = new HttpParams();
    if (includeTextProperties) {
      params = params.set('include_text_properties', 'true');
    }

    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get<DocumentEditAndReadDocument>(this.atlasApiService.buildUrl(`/api/v2/documents/${documentId}`, this.baseUrl), {
        headers,
        params
      })
    );
  }

  getDocumentEditJobs(documentId: number): Observable<DocumentEditJob[]> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get<DocumentEditJob[]>(this.atlasApiService.buildUrl(`/api/v2/documents/${documentId}/document-edit-jobs`, this.baseUrl), {
        headers
      })
    );
  }

  replaceDocumentPiiEntities(documentId: number, entities: DocumentPIIEntityInput[]): Observable<DocumentPIIEntity[]> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.put<DocumentPIIEntity[]>(
        this.atlasApiService.buildUrl(`/api/v2/documents/${documentId}/pii-entities`, this.baseUrl),
        { entities } as ReplaceDocumentPIIEntitiesRequest,
        { headers }
      )
    );
  }

  deleteDocumentPiiEntities(documentId: number): Observable<void> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.delete<void>(this.atlasApiService.buildUrl(`/api/v2/documents/${documentId}/pii-entities`, this.baseUrl), {
        headers
      })
    );
  }

  createDocumentEditJob(documentId: number, payload: SubmitDocumentTranslationRequest): Observable<DocumentEditJob> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.post<DocumentEditJob>(
        this.atlasApiService.buildUrl(`/api/v2/documents/${documentId}/document-edit-jobs`, this.baseUrl),
        payload,
        { headers }
      )
    );
  }

  getDocumentEdit(documentId: number, documentEditJobId: number): Observable<DocumentEdit> {
    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get<DocumentEdit>(
        this.atlasApiService.buildUrl(`/api/v2/documents/${documentId}/document-edit-jobs/${documentEditJobId}/document-edits`, this.baseUrl),
        {
          headers,
          params: new HttpParams().set('include_text_properties', 'true')
        }
      )
    );
  }

  downloadDocumentEdit(documentId: number, documentEditJobId: number, extension?: 'DOCX' | 'PDF'): Observable<string> {
    let params = new HttpParams();
    if (extension) {
      params = params.set('extension', extension);
    }

    return this.atlasApiService.withAtlasAuth(headers =>
      this.http.get(this.atlasApiService.buildUrl(`/api/v2/documents/${documentId}/document-edit-jobs/${documentEditJobId}/document-edits/download`, this.baseUrl), {
        headers,
        params,
        responseType: 'text'
      })
    );
  }

  private createUploadRequest(documentId: string, documentName: string, caseNumber: string): Observable<UploadDocumentRequest> {
    return this.documentService.getFile(documentId, 'application', 'octet-stream').pipe(
      switchMap(file => this.convertBlobToBase64(file)),
      map(base64_encoded_document => ({
        base64_encoded_document,
        document_name: documentName,
        document_type: this.documentType,
        case_number: caseNumber
      }))
    );
  }

  private convertBlobToBase64(file: Blob): Observable<string> {
    return from(new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result;
        if (typeof result !== 'string') {
          reject(new Error('Failed to convert document to Base64.'));
          return;
        }
        resolve(result.split(',').pop() ?? '');
      };
      reader.onerror = () => reject(reader.error ?? new Error('Failed to read document.'));
      reader.readAsDataURL(file);
    }));
  }

}
